import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Users, Handshake, Lightbulb, Globe2, Building2, Award, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export default function OpenInnovationSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  // Parallax scroll effects
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y1 = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-50, 50]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);

  const benefits = [
    { icon: Users, label: t('openInnovation.benefit1'), color: '#3b82f6' },
    { icon: Handshake, label: t('openInnovation.benefit2'), color: '#8DC21F' },
    { icon: Lightbulb, label: t('openInnovation.benefit3'), color: '#f59e0b' },
    { icon: Globe2, label: t('openInnovation.benefit4'), color: '#10b981' },
  ];

  const partners = [
    { name: 'Tecnalia', icon: Building2 },
    { name: 'South Summit', icon: Award },
    { name: 'BackFund', icon: Handshake },
    { name: 'Patio Campus', icon: Building2 },
    { name: 'Area101', icon: Users },
    { name: 'Bind', icon: Globe2 },
  ];

  return (
    <section id="ecosistema" ref={ref} className="py-20 sm:py-28 bg-gray-50 dark:bg-transparent relative overflow-hidden">
      {/* Animated background blobs */}
      <motion.div style={{ y: y1, opacity, scale }} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: 'radial-gradient(circle, #8DC21F 0%, transparent 70%)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-10" style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)' }} />
      </motion.div>

      <motion.div style={{ y: y2 }} className="absolute top-10 right-10 opacity-5">
        <Sparkles size={120} className="text-[#8DC21F]" />
      </motion.div>

      <div className="v-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <motion.span 
            className="v-tag green mb-6 inline-flex"
            whileHover={{ scale: 1.1 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            {t('openInnovation.tag')}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-4 tracking-tight">
            {t('openInnovation.h1a')}{' '}
            <motion.span 
              style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic' }}
              animate={{ 
                backgroundImage: [
                  'linear-gradient(90deg, #8DC21F 0%, #5A8C1A 100%)',
                  'linear-gradient(90deg, #5A8C1A 0%, #8DC21F 100%)',
                  'linear-gradient(90deg, #8DC21F 0%, #5A8C1A 100%)',
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="bg-clip-text text-transparent"
            >
              {t('openInnovation.h1b')}
            </motion.span>
          </h2>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 max-w-3xl mx-auto leading-relaxed">
            {t('openInnovation.sub')}
          </p>
        </motion.div>

        {/* Benefits with advanced animations */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {benefits.map((benefit, i) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 60, rotateX: -20 }}
                animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
                transition={{ 
                  duration: 0.8, 
                  delay: i * 0.15,
                  type: "spring",
                  stiffness: 100
                }}
                whileHover={{ 
                  y: -12, 
                  scale: 1.05,
                  rotateY: 5,
                  transition: { duration: 0.3 }
                }}
                className="liquid-glass rounded-2xl p-6 flex flex-col items-center text-center group cursor-pointer relative overflow-hidden"
              >
                {/* Animated gradient background on hover */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100"
                  style={{ background: `linear-gradient(135deg, ${benefit.color}10 0%, transparent 100%)` }}
                  transition={{ duration: 0.4 }}
                />
                
                <motion.div 
                  className="w-12 h-12 rounded-xl mb-3 flex items-center justify-center relative z-10" 
                  style={{ background: `${benefit.color}15` }}
                  whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.2 }}
                  transition={{ duration: 0.5 }}
                >
                  <Icon size={22} style={{ color: benefit.color }} />
                </motion.div>
                
                <p className="text-sm font-medium text-black dark:text-white/80 relative z-10">{benefit.label}</p>
                
                {/* Shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.6 }}
                />
              </motion.div>
            );
          })}
        </div>

        {/* Partners with stagger and bounce */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="liquid-glass rounded-3xl p-8 relative overflow-hidden"
        >
          {/* Decorative corner accent */}
          <motion.div 
            className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-2xl"
            style={{ background: '#8DC21F' }}
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.05, 0.1, 0.05]
            }}
            transition={{ duration: 4, repeat: Infinity }}
          />

          <h3 className="text-lg font-bold text-black dark:text-white/90 mb-6 text-center relative z-10">
            {t('openInnovation.partnersTitle')}
          </h3>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 relative z-10">
            {partners.map((partner, i) => {
              const Icon = partner.icon;
              return (
                <motion.div 
                  key={i} 
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={inView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ 
                    duration: 0.5, 
                    delay: 0.7 + i * 0.1,
                    type: "spring",
                    stiffness: 200
                  }}
                  whileHover={{ 
                    scale: 1.15, 
                    rotate: [0, -5, 5, 0],
                    transition: { duration: 0.3 }
                  }}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/5 dark:bg-white/5 hover:bg-white/10 dark:hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <motion.div
                    whileHover={{ y: -2 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <Icon size={20} className="text-gray-600 dark:text-white/50" />
                  </motion.div>
                  <span className="text-xs font-semibold text-gray-700 dark:text-white/70 text-center">{partner.name}</span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
